import { useEffect, useState, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Loader from 'react-loaders'
import Sidebar from '../Sidebar/'
import Chatbot from '../Chatbot'
import AvatarCompanion from '../Avatar'
import ThemeToggle from '../ThemeToggle'
import Background from '../Background'
import './index.scss'

const Layout = () => {
  const [showLoader, setShowLoader] = useState(true)
  const { pathname } = useLocation()
  const cursorRef = useRef(null)
  const labelRef = useRef(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false)
    }, 1200)
    return () => clearTimeout(timer)
  }, [])

  // ── "Living ink" cursor ──
  // One organic dot: stretches like a droplet with velocity, morphs into a
  // ring over interactive elements, expands with a mono label (and a gentle
  // magnetic pull) over [data-cursor] targets, becomes an I-beam over text.
  useEffect(() => {
    const cursor = cursorRef.current
    const label  = labelRef.current
    if (!cursor || !label) return

    const INTERACTIVE = 'a, button, [role="button"], [role="link"], label[for], select, summary, input[type="submit"], input[type="button"], input[type="reset"]'
    const TEXTUAL = 'input:not([type="submit"]):not([type="button"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]'

    let mouseX = -200, mouseY = -200, cx = -200, cy = -200, pcx = -200, pcy = -200, rafId
    let magnet = null     // {x, y} — pull target while over a small labeled element
    let morphed = false   // ring / label / text mode → no droplet stretch

    const onMove = (e) => {
      mouseX = e.clientX
      mouseY = e.clientY
    }

    const animate = () => {
      // Magnetic pull toward the hovered target's center
      const tx = magnet ? mouseX + (magnet.x - mouseX) * 0.4 : mouseX
      const ty = magnet ? mouseY + (magnet.y - mouseY) * 0.4 : mouseY
      cx += (tx - cx) * 0.32
      cy += (ty - cy) * 0.32

      let transform = `translate(${cx}px, ${cy}px)`

      // Droplet: stretch along the direction of travel, conserve "volume"
      if (!morphed) {
        const vx = cx - pcx
        const vy = cy - pcy
        const speed = Math.hypot(vx, vy)
        if (speed > 0.5) {
          const stretch = 1 + Math.min(speed * 0.04, 0.65)
          transform += ` rotate(${Math.atan2(vy, vx)}rad) scale(${stretch.toFixed(3)}, ${(1 / Math.sqrt(stretch)).toFixed(3)})`
        }
      }

      pcx = cx
      pcy = cy
      cursor.style.transform = transform
      rafId = requestAnimationFrame(animate)
    }

    // Delegated: works for all elements, including dynamically rendered ones
    const onOver = (e) => {
      const labelEl       = e.target.closest('[data-cursor]')
      const interactiveEl = labelEl ? null : e.target.closest(INTERACTIVE)
      const textEl        = labelEl || interactiveEl ? null : e.target.closest(TEXTUAL)

      cursor.classList.toggle('vk-cursor--label', !!labelEl)
      cursor.classList.toggle('vk-cursor--ring',  !!interactiveEl)
      cursor.classList.toggle('vk-cursor--text',  !!textEl)
      morphed = !!(labelEl || interactiveEl || textEl)

      if (labelEl) {
        label.textContent = labelEl.dataset.cursor
        // Magnetize small targets only — buttons, not full-bleed panels
        const r = labelEl.getBoundingClientRect()
        magnet = r.width < 240 && r.height < 100
          ? { x: r.left + r.width / 2, y: r.top + r.height / 2 }
          : null
      } else {
        magnet = null
      }
    }

    // Press feedback: the ink contracts while the button is held
    const onDown = () => cursor.classList.add('vk-cursor--press')
    const onUp   = () => cursor.classList.remove('vk-cursor--press')

    // Transparent 1×1 cursor — gives the OS something to paint so it never
    // falls back to the arrow during tab-switch / window-focus transitions.
    const BLANK = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E\") 0 0, none"
    const forceHide = () => {
      document.documentElement.style.cursor = BLANK
    }

    // Hide custom cursor when mouse exits the viewport
    const onLeaveWindow = () => {
      cursor.style.opacity = '0'
    }

    // On re-entry: snap position immediately so there's no gap before RAF catches up,
    // then force-hide the native cursor again
    const onEnterWindow = (e) => {
      forceHide()
      cursor.style.opacity = '1'
      if (e.clientX != null) {
        mouseX = e.clientX
        mouseY = e.clientY
        cx = pcx = e.clientX
        cy = pcy = e.clientY
        cursor.style.transform = `translate(${cx}px, ${cy}px)`
      }
    }

    // Tab switch back / window focus: browser re-asserts native cursor briefly — suppress it
    const onVisibilityChange = () => { if (!document.hidden) forceHide() }
    const onFocus = () => forceHide()

    forceHide()
    document.addEventListener('mouseover',         onOver)
    document.addEventListener('mousedown',         onDown)
    document.addEventListener('mouseup',           onUp)
    document.addEventListener('mouseleave',        onLeaveWindow)
    document.addEventListener('mouseenter',        onEnterWindow)
    document.addEventListener('visibilitychange',  onVisibilityChange)
    window.addEventListener('focus',               onFocus)
    window.addEventListener('mousemove',           onMove)
    rafId = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener('mousemove',          onMove)
      window.removeEventListener('focus',              onFocus)
      document.removeEventListener('mouseover',        onOver)
      document.removeEventListener('mousedown',        onDown)
      document.removeEventListener('mouseup',          onUp)
      document.removeEventListener('mouseleave',       onLeaveWindow)
      document.removeEventListener('mouseenter',       onEnterWindow)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      cancelAnimationFrame(rafId)
    }
  }, [])

  return (
    <div className="App">
      {/* Mounted once so the topo plot runs a single time; hidden on Home
          where the pixel portrait leads */}
      <div
        className={`bg-layer${pathname === '/' ? ' bg-layer--hidden' : ''}`}
        aria-hidden="true"
      >
        <Background />
      </div>
      <div ref={cursorRef} className="vk-cursor" aria-hidden="true">
        <span className="vk-cursor__ink">
          <i ref={labelRef} className="vk-cursor__label" />
        </span>
      </div>
      <Sidebar />
      <div className="page">
        <span className="tags top-tags">&lt;body&gt;</span>
        <Outlet />
        <span className="tags bottom-tags">
          &lt;/body&gt;
          <br />
          <span className="bottom-tag-html">&lt;/html&gt;</span>
        </span>
      </div>
      <ThemeToggle />
      <Chatbot />
      <AvatarCompanion />
      {showLoader && <Loader type="pacman" />}
    </div>
  )
}

export default Layout
